'use client';

import IconChevronDown from '@mnd-frontend/icons/components/ChevronDown';
import IconChevronUp from '@mnd-frontend/icons/components/ChevronUp';
import IconSearch from '@mnd-frontend/icons/components/Search';
import { GREY_800, SIZING_6, SPACING_1, SPACING_2 } from '@mnd-frontend/ui/shared/tokens';
import { useState } from 'react';
import styled from 'styled-components';
import { Chip } from '../Chip';
import { InputWrapper } from '../Input';

const SelectedChipsWrapper = styled.div`
  cursor: pointer;
  display: flex;
  gap: ${SPACING_1};
  flex-wrap: wrap;
  padding: ${SPACING_2} ${SIZING_6} ${SPACING_2} ${SPACING_2};
  align-items: flex-start;
`;

const CustomButton = styled.button`
  background: none;
  border: 0;
  color: ${GREY_800};
  flex-shrink: 0;
  font-weight: 300;
  height: ${({ theme }) => theme.sizingButtonHeight};
  line-height: 1.25rem;
  padding: 0;
  text-align: left;
  width: fit-content;
  &:hover {
    color: ${({ theme }) => theme.colorTextButtonTextTextButtonOnLightHover};
  }
`;

const Input = styled.input`
  border: none;
  background: transparent;
  flex: 1;
  height: ${({ theme }) => theme.sizingButtonHeight};
  outline: none;
  padding: 0;
  text-overflow: ellipsis;
  &::placeholder {
    color: ${GREY_800};
    font-weight: 300;
  }
  &:focus,
  &:active {
    &::placeholder {
      color: transparent;
    }
  }
`;

const TagsPlaceholderText = styled.span`
  position: absolute;
  left: ${SPACING_2};
  top: 50%;
  transform: translateY(-50%);
  color: ${GREY_800};
  font-weight: 300;
  pointer-events: none;
  transition: color 0.2s ease-out;
`;

type Chip = {
  value: string;
  label: string;
  disabled?: boolean;
  avatar?: React.ReactNode;
  icon?: React.ReactNode;
};

type Base<T extends Chip> = {
  label: string;
  overflowY?: boolean;
  onClick?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  selectedItems?: T[];
  onRemove?: (id: string) => void;
  dataTestId?: string;
  autoFocus?: boolean;
};

type Input = Base<Chip> & {
  type: 'search';
  searchQuery: string;
  onSearchChange: (query: string) => void;
};

type Tags = Base<Chip> & {
  type: 'tags';
  searchQuery: string;
  onSearchChange: (query: string) => void;
  placeholder: string;
};

type Modal = Base<Chip> & {
  type: 'modal';
};

type DropdownList = Base<Chip> & {
  type: 'dropdownList';
  isOpen: boolean;
};

type Props = Modal | Input | DropdownList | Tags;

export const InputChip = ({
  label,
  overflowY,
  onClick,
  onKeyDown,
  selectedItems = [],
  onRemove,
  dataTestId,
  type,
  autoFocus,
  ...props
}: Props) => {
  const [isTagsFocused, setIsTagsFocused] = useState(false);

  const trailingIcon = {
    dropdownList: 'isOpen' in props ? props.isOpen ? <IconChevronUp /> : <IconChevronDown /> : null,
    search: <IconSearch />,
    modal: <IconSearch />,
    tags: null,
  }[type];

  return (
    <InputWrapper
      hasContent={!!selectedItems}
      trailingIcon={trailingIcon}
      skipMargin
      overflowY={overflowY}
    >
      <SelectedChipsWrapper onClick={() => onClick?.()}>
        {selectedItems?.map(item => {
          if (item.avatar) {
            return (
              <Chip
                key={item.value}
                type="selection"
                onRemove={e => {
                  e?.stopPropagation();
                  onRemove?.(item.value);
                }}
                avatar={item.avatar}
                disabled={item.disabled ?? false}
              >
                {item.label}
              </Chip>
            );
          } else {
            return (
              <Chip
                key={item.value}
                type="selection"
                onRemove={e => {
                  e?.stopPropagation();
                  onRemove?.(item.value);
                }}
                icon={item.icon}
                disabled={item.disabled ?? false}
              >
                {item.label}
              </Chip>
            );
          }
        })}
        {type === 'search' && (
          <Input
            type="text"
            placeholder={label}
            aria-label={label}
            value={'searchQuery' in props ? props.searchQuery : ''}
            onKeyDown={e => onKeyDown?.(e)}
            onChange={e =>
              'onSearchChange' in props ? props.onSearchChange(e.target.value) : null
            }
            autoFocus={autoFocus}
          />
        )}
        {type === 'tags' && (
          <>
            {isTagsFocused &&
              'searchQuery' in props &&
              props.searchQuery.length === 0 &&
              selectedItems.length === 0 && (
                <TagsPlaceholderText>
                  {'placeholder' in props ? props.placeholder : ''}
                </TagsPlaceholderText>
              )}
            <Input
              type="text"
              aria-label={label}
              placeholder={label}
              value={'searchQuery' in props ? props.searchQuery : ''}
              onChange={e =>
                'onSearchChange' in props ? props.onSearchChange(e.target.value) : null
              }
              onKeyDown={e => onKeyDown?.(e)}
              onClick={onClick}
              onFocus={() => setIsTagsFocused(true)}
              onBlur={() => {
                if ('searchQuery' in props && props.searchQuery.length === 0) {
                  setIsTagsFocused(false);
                }
              }}
              autoFocus={autoFocus}
            />
          </>
        )}
        {(type === 'modal' || type === 'dropdownList') && (
          <CustomButton type="button" onClick={onClick} data-testid={dataTestId}>
            {label}
          </CustomButton>
        )}
      </SelectedChipsWrapper>
    </InputWrapper>
  );
};
